
































































































import {
  MobileBookingDTO,
  MobileSubscriptionDTO,
} from '@accounting/interfaces/fallback-mobile-app-payment';
import MobileShoppingCartService, {
  useMobileShoppingCartService,
} from '@accounting/services/mobile-shopping-cart-service';
import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import BpcPaymentResultBookingCard from '@accounting/components/mobile-payment-fallback-result/payment-result-booking-card.vue';
import BpcPaymentResultSubscriptionCard from '@accounting/components/mobile-payment-fallback-result/payment-result-subscription-card.vue';
import BpcPaymentResultCard from '@accounting/components/mobile-payment-fallback-result/payment-result-card.vue';

@Component({
  name: 'MobilePaymentResult',
  components: {
    BpcPaymentResultBookingCard,
    BpcPaymentResultSubscriptionCard,
    BpcPaymentResultCard,
  },
})
export default class MobilePaymentResult extends Vue {
  loaded = false;
  paymentResult = null;
  paymentStatus = 'unpaid';
  queryParams = null;
  subscriptionDetails: MobileSubscriptionDTO = null;
  bookingDetails: MobileBookingDTO = null;
  paymentResultLoaded = false;
  productDetailLoaded = false;
  productDetailError = null;
  errorMessage = null;
  srv: MobileShoppingCartService = null;

  @Prop({ type: [String, Number] }) readonly tenantAlias: string;

  get paymentMethodProvider() {
    return this.queryParams?.payment_method?.toString() || '';
  }

  get stripeSessionId() {
    return this.queryParams?.session_id?.toString() || '';
  }

  get tenantId() {
    return this.queryParams?.tenant_id?.toString() || '';
  }

  get productType() {
    return this.queryParams?.product_type?.toString() || '';
  }

  get productId() {
    return this.queryParams?.product_id?.toString() || '';
  }

  get retryDone() {
    return this.queryParams?.retry_done?.toString() || '';
  }

  get deepLinkUrl() {
    const baseUrl = this.$clientRouter.isLocalHost()
      ? 'http://localhost:8002' // Add http://localhost:8100 later
      : this.$clientRouter.getDeepLinkBaseUrl();

    const queryParams = new URLSearchParams({
      payment_method: this.paymentMethodProvider,
      session_id: this.stripeSessionId,
      tenant_id: this.tenantId,
      product_type: this.productType,
      product_id: this.productId,
      retry_done: 'true',
    });

    return `${baseUrl}/mobile-app/site/${
      this.tenantAlias
    }/payments/result?${queryParams.toString()}`;
  }

  get successPaymentTitle(): string {
    if (!this.paymentResult.success) {
      return '';
    }
    if (this.productType === 'subscription') {
      return this.$t(
        'accounting.payment.mobile_payment_result_success_title_subscription',
      ) as string;
    } else if (this.productType === 'booking') {
      return this.$t('accounting.payment.mobile_payment_result_success_title_booking') as string;
    }
    return '';
  }

  get successPaymentSubtitle() {
    if (!this.paymentResult.success) {
      return '';
    }
    if (this.productType === 'subscription') {
      return this.$t(
        'accounting.payment.mobile_payment_result_success_subtitle_subscription',
      ) as string;
    } else if (this.productType === 'booking') {
      return this.$t('accounting.payment.mobile_payment_result_success_subtitle_booking') as string;
    }
    return '';
  }
  get notSuccessPaymentTitle() {
    if (this.paymentResult.success) {
      return '';
    }
    if (this.productType === 'subscription') {
      return this.$t('accounting.payment.mobile_payment_result_error_title_subscription') as string;
    } else if (this.productType === 'booking') {
      return this.$t('accounting.payment.mobile_payment_result_error_title_booking') as string;
    }
    return '';
  }

  get notSuccessPaymentSubtitle() {
    if (this.paymentResult.success) {
      return '';
    }
    if (this.productType === 'subscription') {
      return this.$t(
        'accounting.payment.mobile_payment_result_error_subtitle_subscription',
      ) as string;
    } else if (this.productType === 'booking') {
      return this.$t('accounting.payment.mobile_payment_result_error_subtitle_booking') as string;
    }
    return '';
  }

  get missingRequiredParams() {
    return (
      !this.tenantId ||
      !this.paymentMethodProvider ||
      !this.stripeSessionId ||
      !this.productType ||
      !this.productId
    );
  }

  created() {
    this.srv = useMobileShoppingCartService();
  }
  async mounted() {
    this.loaded = false;
    this.queryParams = this.$route.query;
    await this.loadTranslations();
    this.checkPaymentStatus();
    this.getProductDetails();
    this.loaded = true;
  }

  async loadTranslations() {
    const lang = localStorage.getItem('lang') || 'fr';
    await this.translationService.getTranslations(lang);
  }

  async wait(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  async manageStripePaymentResult() {
    if (!this.stripeSessionId || !this.tenantId) {
      this.paymentResult = {
        success: false,
        error: this.$t('accounting.payment.error_message.missing_return_url_data'),
      };
      this.paymentResultLoaded = true;
      return;
    }
    // TODO improve this, for the moment we wait 3 seconds
    // to let Stripe update the payment status
    await this.wait(3000);
    try {
      const response = await this.srv.checkStripeSession(this.tenantId, this.stripeSessionId);
      this.paymentStatus = response.payment_status;
      this.paymentResult = {
        success: this.paymentStatus === 'paid',
        error: this.$t('accounting.payment.error_message.stripe_payment_not_paid'),
      };
    } catch (error) {
      this.errorMessage = this.$t('accounting.payment.error_message.stripe_error', { error });
    }
    this.paymentResultLoaded = true;
  }

  async manageSubscriptionPaymentResult() {
    this.paymentStatus = 'paid';
    this.paymentResult = {
      success: true,
      error: false,
    };
    this.paymentResultLoaded = true;
  }

  async manageNoPaymentMethodResult() {
    this.paymentStatus = 'unpaid';
    this.paymentResult = {
      error: 'No payment method provider specified',
    };
    this.paymentResultLoaded = true;
  }

  async checkPaymentStatus() {
    this.paymentResultLoaded = false;
    this.errorMessage = null;
    // Check payment result done with Stripe
    if (this.paymentMethodProvider.startsWith('stripe')) {
      return await this.manageStripePaymentResult();
    } else if (this.paymentMethodProvider.startsWith('subscription')) {
      return await this.manageSubscriptionPaymentResult();
    } else {
      return await this.manageNoPaymentMethodResult();
    }
  }

  async getProductDetails() {
    this.productDetailError = null;
    this.productDetailLoaded = false;
    if (this.productType === 'subscription' && this.productId) {
      try {
        const response = await this.srv.fetchSubscriptionDetails(this.tenantId, this.productId);
        this.subscriptionDetails = response;
      } catch {
        this.productDetailError = this.$t(
          'accounting.payment.error_message.product_result_with_error',
        );
      }
    }
    if (this.productType === 'booking' && this.productId) {
      try {
        const response = await this.srv.fetchBookingDetails(this.tenantId, this.productId);
        this.bookingDetails = response;
      } catch {
        this.productDetailError = this.$t(
          'accounting.payment.error_message.product_result_with_error',
        );
      }
    }
    this.productDetailLoaded = true;
  }
}
